<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox
          title="Resource Group"
          :loading="pageDataLoading"
          :avatarImage="logoURL"
          :avatarName="group.name"
          :avatarCircleMode="avatarCircleMode"
          :avatarEditable="userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)"
          @editImage="editAvatarImage"
          @removeImage="removeAvatarImage">
          <h4>{{group.name}}</h4>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <span v-if="group.isActive" class="status-active">Active</span>
              <span v-if="!group.isActive" class="status-archive">Archived</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Highlight">
              <LabelBadge :labelColour="group.highlightColour">{{group.name}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Type">{{group.type | formatLookupName(ResourceGroupTypeLookup)}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{group.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{group.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-group-summary">
              <i class="far fa-clipboard-list-check"></i> Resource Group Details
            </Tab>
            <Tab tabid="tab-group-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>
          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-group-summary">
              
              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)" class="float-right">
                    <DropdownMenuItem @click="editGroup" icon="fa-pencil">Edit Resource Group</DropdownMenuItem>

                    <DropdownMenuDivider />
                    <DropdownMenuItem @click="archiveGroup" v-if="group.isActive" icon="fa-archive">Archive Resource Group</DropdownMenuItem>
                    <DropdownMenuItem @click="restoreGroup" v-if="!group.isActive" icon="fa-undo">Restore Resource Group</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Name">{{group.name}}</DataItem>
                  <DataItem title="Category">{{group.type | formatLookupName(ResourceGroupTypeLookup)}}</DataItem>
                </template>
              </DataPageArea>

              <div class="row mt-2">
                <div class="col-lg-12">
                  <h3>Resources</h3>
                  <StaticDataTable v-if="group.resources">
                    <template v-slot:header>
                      <tr>
                        <th>Resource</th>
                        <th>Type</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="resource of group.resources" :key="resource.id">
                        <td>
                          <LinkResource :id="resource.id" :name="resource.name" />
                        </td>
                        <td>{{resource.type | formatLookupName(ResourceTypeLookup)}}</td>
                      </tr>
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-group-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.resourceGroup" :linkId="group.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import EditLogoImageDialog from '@/modules/core/views/editLogoImageDialog.vue'
import ResourceGroupDialog from '@/modules/tasks/views/resourceGroupDialog'

import { LinkTypes, ResourceGroupTypeLookup, ResourceTypeLookup } from '@/store/types'

export default {
  name: 'ResourceGroup',
  data() {
    return {
      group: {}
    }
  },
  computed: {
    logoURL() {
      if(this.$data.group.logoURL == null || this.$data.group.logoURL == '') {
        return '/glass/img/tasks-overview-resourcegroup.png'
      }
      return this.$data.group.logoURL
    },
    avatarCircleMode() {
      return this.$data.group.logoURL != null && this.$data.group.logoURL != ''
    },
    activityStreamURL() {
      return `/tasks/resourcegroups/${this.$route.params.id}/activitystream`
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.ResourceGroupTypeLookup = ResourceGroupTypeLookup
    this.ResourceTypeLookup = ResourceTypeLookup
  },
  mounted() {
    this.requestData()
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.pageLoadData(this.$apiService.tasksService.getResourceGroup(this.$route.params.id))
      .then(response => {
        this.group = response.data
      })
      .catch(err => this.handlePageLoadError(err))
    },

    editGroup() {
      this.displayModal(ResourceGroupDialog, { 
        resourceGroupId: this.$data.group.id,
      },
      this.$data.group)
      .then((success) => {
        if(success) {
          this.successToast('Resource Group Updated')
          this.reloadData()
        }
      })
    },
    archiveGroup() {
      this.confirmDeleteMessage('Archive Resource Group?', 'Are you sure you want to archive this resource group?')
      .then(success => {
        if(success) {
          this.$apiService.tasksService.archiveResourceGroup(this.$route.params.id).then(response => {
            this.successToast('Resource Group Archived')
            this.reloadData()
          })
          .catch(err => { this.displayNetworkError(err, 'Error', 'Could not archive resource group') })
        }
      })
    },
    restoreGroup() {
      this.confirmDeleteMessage('Restore Resource Group?', 'Are you sure you want to restore this resource group?')
      .then(success => {
        if(success) {
          this.$apiService.tasksService.archiveResourceGroup(this.$route.params.id).then(response => {
            this.successToast('Resource Group Restored')
            this.reloadData()
          })
          .catch(err => { this.displayNetworkError(err, 'Error', 'Could not restore resource group') })
        }
      })
    },

    editAvatarImage() {
      this.displayModal(EditLogoImageDialog, { 
        resourceId: this.$data.group.id,
        APIresource: 'tasks/resourcegroups',
        currentURL: this.$data.group.logoURL,
        circleCrop: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Logo Updated')
          this.requestData()
        }
      })
    },
    removeAvatarImage() {
      this.confirmDeleteMessage('Remove Image?', 'Are you sure you remove this group\'s image?')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLogoImage(this.$data.group.id, 'tasks/resourcegroups').then(response => {
            this.successToast('Image Removed')
            this.requestData()
          })
        }
      })
    }
  }
}
</script>